import React, {memo} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {twMerge} from 'tailwind-merge';
import {usePopoverContext} from './utils';

/**
 * PopoverContent - The content that appears in the popover
 *
 * @typedef {Object} PopoverContentProps
 * @property {React.ReactNode} children - Content to display in the popover
 * @property {string} [className] - Additional CSS classes
 * @property {string} [portalTarget] - Portal target element ID (default: 'popper')
 * @property {function} [onClick] - Click handler (default: stopPropagation to prevent closing)
 */
const PopoverContent = memo(
	({children, className, portalTarget = 'popper', onClick, ...props}) => {
		const {open, isClosing, popperRef, styles, attributes, setOpen, setIsClosing} =
			usePopoverContext();

		const handleClick =
			onClick ||
			(e => {
				// Prevent closing when clicking inside menu by default
				e.stopPropagation();
			});

		const handleKeyDown = e => {
			if (e.key === 'Escape') {
				setIsClosing(true);
				setOpen(false);
				setTimeout(() => setIsClosing(false), 150);
			}
		};

		if (!open) return null;

		const content = (
			<div
				ref={popperRef}
				data-popover-menu="true"
				style={styles.popper}
				{...attributes.popper}
				className={twMerge(
					'z-[500] mt-1 min-w-[240px] bg-white dark:bg-gray-900 border overflow-hidden border-gray-200 dark:border-gray-700 rounded-md shadow-lg',
					isClosing && 'pointer-events-none',
					className,
				)}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				role="menu"
				{...props}
			>
				{children}
			</div>
		);

		const portalElement = document.getElementById(portalTarget);
		if (!portalElement) {
			console.warn(
				`Portal target "${portalTarget}" not found. Rendering popover content inline.`,
			);
			return content;
		}

		return createPortal(content, portalElement);
	},
);

PopoverContent.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	portalTarget: PropTypes.string,
	onClick: PropTypes.func,
};

PopoverContent.displayName = 'PopoverContent';

export default PopoverContent;
